import { LoadingOutlined } from "@ant-design/icons";
import { Layout } from "antd";
import { Content } from "antd/es/layout/layout";
import { compact } from "lodash";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { Suspense, lazy, useEffect } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { ROUTES } from "../../common/constants";
import Header from "../../components/Header";
import Sidenav from "../../components/Sidenav";
import Spinner from "../../components/Spinner";
import Topnav from "../../components/Topnav";
import { dashboardStores } from "./DashboardStores";
const Application = lazy(() => import("../Application"));
const ApplicationDetail = lazy(() => import("../ApplicationDetail"));
const Crashlytics = lazy(() => import("../Crashlytics"));
const FlowList = lazy(() => import("../Engagement/Flow/FlowList"));
const FlowStudio = lazy(() => import("../Engagement/Flow/FlowStudio"));
const FlowReport = lazy(() => import("../Engagement/Flow/FlowReport"));
const CampaignList = lazy(() => import("../Engagement/Channel/CampaignList"));
const CampaignForm = lazy(() => import("../Engagement/Channel/CampaignForm"));
const CampaignDetail = lazy(() => import("../Engagement/Channel/CampaignDetail"));
const Deeplink = lazy(() => import("../Deeplink"));
const DeeplinkOverview = lazy(() => import("../DeeplinkOverview"));
const Devices = lazy(() => import("../Devices"));
const EventDetail = lazy(() => import("../EventDetail"));
const Events = lazy(() => import("../Events"));
const ProfileView = lazy(() => import("../ProfileView"));
const Account = lazy(() => import("../Setting/Account"));
const RoleSetting = lazy(() => import("../Setting/Role"));
const AccessToken = lazy(() => import("../Setting/AccessToken"));
const UnifiedCustomerProfiles = lazy(() => import("../UnifiedCustomerProfiles"));
const Shortlink = lazy(() => import("../Shortlink"));
const UserSegment = lazy(() => import("../UserSegment"));
const UserSegmentAction = lazy(() => import("../UserSegmentAction"));
const Email = lazy(() => import("../Email"));
const TopicPush = lazy(() => import("../TopicPush"));
const FlagConfig = lazy(() => import("../FlagConfig"));
const ABTesting = lazy(() => import("../ABTesting"));
const ExperimentDetail = lazy(() => import("../ABTesting/components/ExperimentDetail"));
const PageNotFound = lazy(() => import("../PageNotFound"));
const EngagementPath = lazy(() => import("../EngagementPath"));
const ReferralReport = lazy(() => import("../ReferralReport"));
const CampaignManagement = lazy(() => import("../CampaignManagement"));
const AdInventory = lazy(() => import("../AdInventory"));
const AdApproval = lazy(() => import("../AdApproval"));
const AdCampaign = lazy(() => import("../AdCampaign"));
const Ad = lazy(() => import("../Ad"));
const Product = lazy(() => import("../Product"));
const ReportAdvertiser = lazy(() => import("../ReportAdvertiser"));
const ReportRetailer = lazy(() => import("../ReportRetailer"));
const Banner = lazy(() => import("../Banner"));
const AuditLog = lazy(() => import("../AuditLog"));
const TrackingFilter = lazy(() => import("../TrackingFilter"));

const privateRouteStore = compact([
  { path: ROUTES.dashboard, component: <Application /> },
  { path: ROUTES.application, component: <Application /> },
  { path: ROUTES.appOverview, component: <ApplicationDetail /> },
  { path: ROUTES.appEvents, component: <Events /> },
  { path: ROUTES.appEventDetail, component: <EventDetail /> },
  { path: ROUTES.appDevices, component: <Devices /> },
  { path: ROUTES.campaignManagement, component: <CampaignManagement /> },
  { path: ROUTES.campaignReport, component: <ReferralReport /> },
  { path: ROUTES.appCrashlytics, component: <Crashlytics /> },

  { path: ROUTES.appDeeplink, component: <Deeplink /> },
  { path: ROUTES.overviewDeeplink, component: <DeeplinkOverview /> },
  { path: ROUTES.shortlink, component: <Shortlink /> },

  { path: ROUTES.engagement, component: <FlowList /> },
  { path: ROUTES.studio, component: <FlowStudio /> },
  { path: ROUTES.flowReport, component: <FlowReport /> },
  { path: ROUTES.engagementPath, component: <EngagementPath /> },
  { path: ROUTES.pushOption, component: <CampaignList /> },
  { path: ROUTES.pushOptionCreate, component: <CampaignForm /> },
  { path: ROUTES.pushOptionEdit, component: <CampaignForm /> },
  { path: ROUTES.pushOptionView, component: <CampaignDetail /> },
  process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION"
    ? { path: ROUTES.flagConfig, component: <FlagConfig /> }
    : undefined,
  process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION" ? { path: ROUTES.abTesting, component: <ABTesting /> } : undefined,
  process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION"
    ? { path: ROUTES.experiment, component: <ExperimentDetail /> }
    : undefined,
  { path: ROUTES.emailMarketing, component: <Email /> },
  { path: ROUTES.topicPush, component: <TopicPush /> },
  { path: ROUTES.userSegment, component: <UserSegment /> },
  { path: ROUTES.userSegmentDetail, component: <TrackingFilter /> },
  { path: ROUTES.userSegmentCreate, component: <UserSegmentAction /> },
  { path: ROUTES.userSegmentEdit, component: <UserSegmentAction /> },
  { path: ROUTES.unifiedCustomerProfiles, component: <UnifiedCustomerProfiles /> },
  { path: ROUTES.profileView, component: <ProfileView /> },

  { path: ROUTES.retailerStatistics, component: <ReportRetailer /> },
  { path: ROUTES.adInventory, component: <AdInventory /> },
  { path: ROUTES.adApproval, component: <AdApproval /> },
  { path: ROUTES.advertiserStatistics, component: <ReportAdvertiser /> },
  { path: ROUTES.adCampaign, component: <AdCampaign /> },
  { path: ROUTES.ad, component: <Ad /> },
  { path: ROUTES.product, component: <Product /> },
  { path: ROUTES.banner, component: <Banner /> },

  { path: ROUTES.accounts, component: <Account /> },
  { path: ROUTES.role, component: <RoleSetting /> },
  { path: ROUTES.accessToken, component: <AccessToken /> },

  { path: ROUTES.auditLog, component: <AuditLog /> },
]);

const publicRouteStore = [];

const Dashboard = observer(() => {
  const history = useHistory();
  const availableRoutes = toJS(dashboardStores.availableRoutes);

  useEffect(() => {
    dashboardStores.prepareRoute(privateRouteStore, publicRouteStore);
    dashboardStores.getCampaignTags();
  }, []);

  if (!availableRoutes.length) return <Spinner />;

  return (
    <Layout className="!min-h-screen">
      {/* <Chatbot /> */}
      <Sidenav path={history.location.pathname} />
      <Layout>
        <Topnav />
        <Header />
        <Suspense
          fallback={
            <div className="h-full flex-center">
              <LoadingOutlined className="text-main text-4xl" />
            </div>
          }
        >
          <Content>
            <Switch>
              {availableRoutes.map((route) => (
                <Route key={route.path} path={route.path} exact={true}>
                  {route.component}
                </Route>
              ))}
              <Route key={"page_not_found"} path="*" exact={true}>
                <PageNotFound />
              </Route>
            </Switch>
          </Content>
        </Suspense>
      </Layout>
    </Layout>
  );
});

export default Dashboard;
